// Tela da licitação — o processo inteiro de um edital público.
//
// Nasceu (F3) como a tela que substituía o NegocioDetalhe quando a proposta
// tinha origem='licitacao', aberta pelo pipeline PRIVADO e chaveada pelo
// negócio (`propostaId`). Desde 02/10/2026 ela abre pela OPORTUNIDADE, de
// dentro do pipeline de licitações (pipeline/PipelineLicitacoes.tsx), e ganhou
// o cabeçalho do processo: trilha de etapas com tempo na etapa, próximo prazo,
// responsável e os botões de decisão (Vamos participar · Não participamos ·
// Perdido · Contrato assinado). O negócio gêmeo (`proposta_id`) só existe a
// partir da etapa Proposta — Checklist e Composições aparecem quando ele existe
// (o checklist por oportunidade é a Fase 3 do plano, docs/licitacoes-pipeline.md).
// `propostaId` continua aceito para quem ainda chega pelo caminho antigo.
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Tabs, Card, Button, Typography, Tag, Space, Spin, Empty, Alert, Input,
  Select, App as AntApp, Row, Col, Statistic, Tooltip, Divider, Modal, Upload,
  Form, Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined, FileTextOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ExclamationCircleOutlined, QuestionCircleOutlined,
  ReloadOutlined, RobotOutlined, DownloadOutlined, EditOutlined,
  UploadOutlined, PlusOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDataBr } from '../../utils/dataFmt';
// L2 (2026-06-02): reaproveita os componentes de composicoes do privado.
import { AbaComposicoes } from '../negocio-detalhe/AbaComposicoes';
import ModalNovaComposicao from '../ModalNovaComposicao';
import { EditorComposicaoInline } from '../ComercialComposicoes';
import { usePerms } from '../../hooks/usePerms';
import { licApi, erroDe, type Vendedor } from './pipeline/api';
import { TrilhaLicitacao } from './pipeline/TrilhaLicitacao';
import { ModalFecharLicitacao, type TipoFechamento, type DadosFechamento } from './pipeline/ModalFecharLicitacao';
import { ehTriagem, ehTerminal, ehAberta, etapaDe, rotuloDe, pedeMotivo, normalizar } from './pipeline/etapas';
import { proximoPrazo, diasNaEtapa, type LicitacaoPipeline } from './pipeline/saudeLicitacao';
import { valorCompacto } from '../comercial-pipeline/saudeNegocio';

const { Title, Text, Paragraph } = Typography;

interface ChecklistItem {
  id: number;
  proposta_id: number;
  categoria: string;
  subcategoria: string | null;
  descricao: string;
  exigencia_edital: string | null;
  status: 'temos_atende' | 'temos_atencao' | 'falta' | 'pendente' | 'nao_aplicavel';
  doc_id_vinculado: number | null;
  doc_codigo: string | null;
  doc_titulo: string | null;
  doc_categoria_nome: string | null;
  doc_download_url?: string | null;
  doc_arquivo_nome?: string | null;
  // F8 — template docx editavel vinculado a este item
  doc_eh_template?: boolean | null;
  doc_template_campos?: any[] | null;
  observacoes: string | null;
  ordem: number;
  editado_manualmente: boolean;
  // L8 (2026-06-02) — anexo do edital que originou este item
  anexo_origem_id?: number | null;
  anexo_origem_nome?: string | null;
  anexo_origem_tipo?: string | null;
  anexo_origem_download_url?: string | null;
}

interface ChecklistMeta {
  proposta_id: number;
  status: 'pendente' | 'gerando' | 'pronto' | 'erro';
  gerado_em: string | null;
  gerado_modelo: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  custo_estimado_usd: number | null;
  total_itens: number | null;
  itens_completos: number | null;
  erro_mensagem: string | null;
}

interface OportunidadeData {
  status_interno?: string;
  etapa_desde?: string | null;
  responsavel_email?: string | null;
  prazo_efetivo?: string | null;
  proximo_prazo?: string | null;
  proximo_prazo_tipo?: string | null;
  decisao_motivo?: string | null;
  motivo_perda?: string | null;
  vencedor?: string | null;
  preco_vencedor?: number | null;
  valor_contratado?: number | null;
  conlic_url?: string | null;
  id: number;
  numero_edital: string | null;
  orgao: string | null;
  uf: string | null;
  cidade: string | null;
  modalidade: string | null;
  objeto: string | null;
  data_abertura: string | null;
  valor_estimado: number | null;
  fit_score: number | null;
  fit_resumo: string | null;
  proposta_id: number | null;
}

interface AnexoEdital {
  id: number;
  nome: string;           // coluna real no banco
  tipo: string;
  s3_key: string;
  download_url?: string | null;  // presigned URL (10min) gerada pelo backend
  metadados?: any;
  criado_em?: string;
  // F5.3 — status de extração de exigências pelo Haiku
  exigencias_status?: 'pendente' | 'extraindo' | 'pronto' | 'erro' | null;
  exigencias_total?: number | null;
  exigencias_erro?: string | null;
}

const STATUS_CFG: Record<string, { label: string; color: string; icon: any }> = {
  temos_atende:   { label: 'Atende',     color: 'green',  icon: <CheckCircleOutlined /> },
  temos_atencao:  { label: 'Atenção',    color: 'orange', icon: <ExclamationCircleOutlined /> },
  falta:          { label: 'Falta',      color: 'red',    icon: <CloseCircleOutlined /> },
  pendente:       { label: 'Pendente',   color: 'blue',   icon: <QuestionCircleOutlined /> },
  nao_aplicavel:  { label: 'N/A',        color: 'default',icon: <QuestionCircleOutlined /> },
};

const CATEGORIA_LABEL: Record<string, string> = {
  tecnica:      'Técnica',
  financeira:   'Financeira',
  fiscal:       'Fiscal',
  trabalhista: 'Trabalhista',
  cadastral:   'Cadastral',
  profissionais: 'Profissionais',
  outros:      'Outros',
};

export default function TelaLicitacao({ oportunidadeId, propostaId: propostaIdProp, onVoltar, onMudou }: {
  /** Oportunidade (caminho novo, pelo pipeline de licitações). */
  oportunidadeId?: number;
  /** Negócio gêmeo (caminho antigo). Um dos dois é obrigatório. */
  propostaId?: number;
  onVoltar: () => void;
  /** Avisa o pipeline quando a etapa/responsável mudou (linha nova) ou pede recarga (null). */
  onMudou?: (nova: LicitacaoPipeline | null) => void;
}) {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeOperar = pode('comercial.licitacoes.pipeline.operar');
  // Gerar o checklist custa IA: mesma régua do servidor (operar o pipeline, ou
  // a permissão antiga do catálogo), e só com a licitação aberta (05/10/2026).
  const podeGerarChecklist = podeOperar || pode('comercial.negocio_licitacao.gerar_checklist');
  const [oport, setOport] = useState<OportunidadeData | null>(null);
  // O gêmeo: o que a oportunidade aponta, ou o que veio pelo caminho antigo.
  const propostaId: number | null = oport?.proposta_id ?? propostaIdProp ?? null;
  const [vendedores, setVendedores] = useState<Vendedor[]>([]);
  const [fechando, setFechando] = useState<TipoFechamento | null>(null);
  const [movendo, setMovendo] = useState(false);
  const [anexos, setAnexos] = useState<AnexoEdital[]>([]);
  const [checklistItens, setChecklistItens] = useState<ChecklistItem[]>([]);
  const [checklistMeta, setChecklistMeta] = useState<ChecklistMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadando, setUploadando] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [baixandoZip, setBaixandoZip] = useState(false);
  // F8 — geração de declaração via IA (sem modal de preenchimento)
  const [gerandoDocxItemId, setGerandoDocxItemId] = useState<number | null>(null);
  // F10 — modal "Adicionar template específico desta licitação"
  const [templateLicModal, setTemplateLicModal] = useState(false);
  const [tplTitulo, setTplTitulo] = useState('');
  const [tplArquivo, setTplArquivo] = useState<File | null>(null);
  const [tplCategoria, setTplCategoria] = useState('outros');
  const [tplSalvando, setTplSalvando] = useState(false);
  const [tplCategorias, setTplCategorias] = useState<any[]>([]);
  // L2 (2026-06-02) — composicoes inline na tela de licitacao
  const [modalNovaComposicao, setModalNovaComposicao] = useState(false);
  const [composicaoAbertaId, setComposicaoAbertaId] = useState<string | null>(null);
  const [reloadComposicoes, setReloadComposicoes] = useState(0);
  // L6 (2026-06-02) — filtros do checklist
  const [filtroStatus, setFiltroStatus] = useState<string[]>([]);
  const [filtroCategoria, setFiltroCategoria] = useState<string[]>([]);
  const [filtroBusca, setFiltroBusca] = useState('');
  // L7 (2026-06-02) — upload de doc especifico no item
  const [uploadItemId, setUploadItemId] = useState<number | null>(null);
  const [uploadItemCategoria, setUploadItemCategoria] = useState<string>('');
  const [uploadItemDescricao, setUploadItemDescricao] = useState<string>('');
  const [uploadItemTitulo, setUploadItemTitulo] = useState('');
  const [uploadItemArquivo, setUploadItemArquivo] = useState<File | null>(null);
  const [uploadItemSalvando, setUploadItemSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const headers = { Authorization: token };

      // Pela oportunidade (caminho novo) ou pelo gêmeo (caminho antigo):
      // `detalhe` traz a ficha inteira — etapa, responsável, prazo, anexos.
      let id = oportunidadeId || null;
      if (!id && propostaIdProp) {
        const rOp = await axios.post(`${API_URL}/comercial/licitacoes`,
          { action: 'oportunidade_por_proposta', proposta_id: propostaIdProp }, { headers });
        const lic = rOp.data?.body ? JSON.parse(rOp.data.body) : rOp.data;
        id = lic?.id || null;
      }
      if (!id) { setOport(null); return; }
      const rDet = await axios.post(`${API_URL}/comercial/licitacoes`, { action: 'detalhe', id }, { headers });
      const det = rDet.data.body ? JSON.parse(rDet.data.body) : rDet.data;
      setOport(det && det.id ? det : null);
      setAnexos(Array.isArray(det?.anexos) ? det.anexos : []);

      // 🚨 (05/10/2026) O checklist é da LICITAÇÃO, não do negócio gêmeo:
      // existe desde Avaliando/Preparação. Antes só aparecia em Proposta (o
      // gêmeo nasce lá) — e o Lucas não achava o botão no Sergipe.
      if (det?.id) {
        const rChk = await axios.post(`${API_URL}/comercial/licitacoes`,
          { action: 'listar_checklist', oportunidade_id: det.id }, { headers });
        const chk = rChk.data.body ? JSON.parse(rChk.data.body) : rChk.data;
        setChecklistMeta(chk?.meta || null);
        setChecklistItens(chk?.itens || []);
      } else {
        setChecklistMeta(null);
        setChecklistItens([]);
      }
    } catch (e: any) {
      message.error('Erro ao carregar: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [oportunidadeId, propostaIdProp, message]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { licApi.vendedores().then(setVendedores).catch(() => { /* fica o e-mail */ }); }, []);

  // ── Etapa, decisão e responsável (02/10/2026) ──────────────────────────
  const etapaAtual = normalizar(oport?.status_interno);
  const linhaAtual = oport as unknown as LicitacaoPipeline | null;
  const prazo = linhaAtual ? proximoPrazo(linhaAtual) : null;
  const dias = linhaAtual ? diasNaEtapa(linhaAtual) : null;

  async function aplicar(fn: () => Promise<any>, sucesso: string) {
    setMovendo(true);
    try {
      const nova = await fn();
      message.success(sucesso);
      onMudou?.(nova && nova.id ? nova : null);
      await carregar();
      return true;
    } catch (e: any) {
      message.error(erroDe(e));
      return false;
    } finally { setMovendo(false); }
  }
  const avaliar = () => oport && aplicar(() => licApi.avaliar(oport.id), 'Entrou no pipeline em Avaliando.');
  const participar = () => oport && aplicar(() => licApi.decidir(oport.id, true), 'Vamos participar — etapa Preparação.');
  function pedirMovimento(para: string) {
    if (!oport) return;
    if (pedeMotivo(para) || para === 'ganho') { setFechando(para as TipoFechamento); return; }
    aplicar(() => licApi.mover(oport.id, para), `Etapa: ${rotuloDe(para)}`);
  }
  async function confirmarFechamento(dados: DadosFechamento) {
    if (!oport || !fechando) return;
    const okMov = await aplicar(
      () => (fechando === 'nao_participamos' && (etapaAtual === 'avaliando' || ehTriagem(etapaAtual)))
        ? licApi.decidir(oport.id, false, dados.motivo)
        : licApi.mover(oport.id, fechando, dados),
      `Registrado: ${rotuloDe(fechando)}.`);
    if (okMov) setFechando(null);
  }
  const trocarResponsavel = (email: string | undefined) => oport && aplicar(
    () => licApi.atualizar(oport.id, { responsavel_email: email || '' }), 'Responsável atualizado.');

  // Polling enquanto checklist gerando
  useEffect(() => {
    if (checklistMeta?.status !== 'gerando') return;
    const i = setInterval(carregar, 8000);
    return () => clearInterval(i);
  }, [checklistMeta?.status, carregar]);

  async function gerarChecklist() {
    if (!oport) return;
    setGerando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'gerar_checklist', oportunidade_id: oport.id },
        { headers: { Authorization: token } });
      message.success('Checklist sendo gerado. Demora 60-180s.');
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally {
      setGerando(false);
    }
  }

  async function atualizarItem(itemId: number, campo: string, valor: any) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'atualizar_item_checklist', id: itemId, [campo]: valor },
        { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) {
      message.error('Erro ao atualizar: ' + (e.response?.data?.error || e.message));
    }
  }

  async function baixarAnexo(anexo: AnexoEdital) {
    if (anexo.download_url) {
      window.open(anexo.download_url, '_blank');
      return;
    }
    message.warning('URL não gerada — recarregue a tela.');
  }

  // Heuristica de tipo pelo nome do arquivo (case-insensitive).
  // Ordem importa: testa do mais especifico pro mais generico.
  function inferirTipoEdital(filename: string): string {
    const n = filename.toLowerCase();
    if (/edital/.test(n) && !/anexo/.test(n)) return 'Edital';
    if (/(termo[\s_-]*de[\s_-]*referencia|^tr[\s_._-]|[\s_-]tr[\s_._-]|_tr\.)/i.test(n)) return 'Termo de Referência';
    if (/projeto[\s_-]*basico|projeto[\s_-]*b[áa]sico/.test(n)) return 'Projeto Básico';
    if (/planilha|orcament|or[çc]ament/.test(n)) return 'Planilha Orçamentária';
    if (/cronograma/.test(n)) return 'Cronograma';
    if (/minuta[\s_-]*contrato|contrato[\s_-]*minuta/.test(n)) return 'Minuta de Contrato';
    if (/anexo/.test(n)) return 'Anexo';
    return 'Outros';
  }

  async function uploadAnexoManual() {
    if (!oport) return;
    if (!uploadFiles.length) { message.warning('Selecione ao menos um arquivo'); return; }
    setUploadando(true);
    setUploadProgress('');
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      let okCount = 0;
      let erroCount = 0;
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        const nomeBase = file.name.replace(/\.[^.]+$/, '');
        const tipo = inferirTipoEdital(file.name);
        setUploadProgress(`${i + 1}/${uploadFiles.length} — ${file.name} (${tipo})`);
        try {
          const r1 = await axios.post(`${API_URL}/comercial/licitacoes`,
            { action: 'upload_url_anexo_edital', oportunidade_id: oport.id,
              filename: file.name, content_type: file.type || 'application/pdf' },
            { headers });
          const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
          await axios.put(d1.upload_url, file, {
            headers: { 'Content-Type': file.type || 'application/pdf' },
          });
          await axios.post(`${API_URL}/comercial/licitacoes`,
            { action: 'criar_anexo_edital', oportunidade_id: oport.id,
              s3_key: d1.s3_key, nome: nomeBase, tipo },
            { headers });
          okCount++;
        } catch (e: any) {
          erroCount++;
          console.error(`Erro em ${file.name}:`, e.response?.data?.error || e.message);
        }
      }
      if (erroCount === 0) {
        message.success(`${okCount} arquivo(s) anexado(s).`);
      } else {
        message.warning(`${okCount} OK, ${erroCount} com erro. Veja console pra detalhes.`);
      }
      setUploadModal(false);
      setUploadFiles([]);
      await carregar();
    } catch (e: any) {
      message.error('Erro geral: ' + (e.response?.data?.error || e.message));
    } finally {
      setUploadando(false);
      setUploadProgress('');
    }
  }

  // F8 — clica "Gerar declaração" e a IA preenche o docx automaticamente
  // com dados Hydrodata + dados da licitação. Sem formulário intermediário.
  async function gerarDocxTemplate(item: ChecklistItem) {
    if (!item.doc_id_vinculado || !item.doc_eh_template) return;
    setGerandoDocxItemId(item.id);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'gerar_documento_template',
          doc_id: item.doc_id_vinculado,
          // A licitação primeiro: com ela a declaração sai com os dados do
          // edital mesmo antes de o negócio gêmeo existir.
          oportunidade_id: oport?.id,
          proposta_id: propostaId,
          checklist_item_id: item.id },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.download_url) {
        window.open(d.download_url, '_blank');
        const msgs = [`Declaração gerada (${d.substituicoes_aplicadas}/${d.substituicoes_total} preenchimentos).`];
        if (d.atencao) msgs.push('⚠ ' + d.atencao);
        if (Array.isArray(d.nao_encontradas) && d.nao_encontradas.length) {
          msgs.push(`${d.nao_encontradas.length} trecho(s) não localizado(s) — revise o .docx no Word.`);
        }
        message.success(msgs.join(' '));
      } else {
        message.error('Falha ao gerar.');
      }
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally {
      setGerandoDocxItemId(null);
    }
  }

  // F10 — abre modal de adicionar template especifico (carrega categorias)
  async function abrirAddTemplateLic() {
    setTplTitulo('');
    setTplArquivo(null);
    setTplCategoria('outros');
    setTemplateLicModal(true);
    if (!tplCategorias.length) {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/documentos`,
          { action: 'categorias' }, { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setTplCategorias(Array.isArray(d?.categorias) ? d.categorias : []);
      } catch { /* silent */ }
    }
  }

  async function salvarTemplateLic() {
    if (!oport) return;
    if (!tplTitulo.trim()) { message.warning('Título obrigatório'); return; }
    if (!tplArquivo) { message.warning('Selecione o arquivo .docx'); return; }
    if (!/\.docx$/i.test(tplArquivo.name)) {
      message.warning('Apenas .docx aceito como modelo editável pela IA'); return;
    }
    setTplSalvando(true);
    try {
      const token = await getToken();
      // 1) upload presigned
      const r1 = await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'upload_url',
          filename: tplArquivo.name,
          content_type: tplArquivo.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          categoria_codigo: tplCategoria,
        }, { headers: { Authorization: token } });
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      // 2) PUT no S3
      await axios.put(d1.upload_url, tplArquivo, {
        headers: { 'Content-Type': tplArquivo.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
      });
      // 3) criar documento com escopo + eh_template
      await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'criar',
          s3_key: d1.s3_key,
          filename_original: tplArquivo.name,
          titulo: tplTitulo,
          categoria_codigo: tplCategoria,
          eh_template: true,
          escopo_oportunidade_id: oport.id,
          template_campos: [],
        }, { headers: { Authorization: token } });
      message.success('Template adicionado. Regenere o checklist pra vincular automaticamente.');
      setTemplateLicModal(false);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally {
      setTplSalvando(false);
    }
  }

  // L7 (2026-06-02) — abre modal de upload pra um item especifico do checklist
  function abrirUploadItem(item: ChecklistItem) {
    setUploadItemId(item.id);
    setUploadItemCategoria(item.categoria || 'outros');
    setUploadItemDescricao(item.descricao || '');
    setUploadItemTitulo(item.descricao || '');
    setUploadItemArquivo(null);
  }
  function fecharUploadItem() {
    if (uploadItemSalvando) return;
    setUploadItemId(null);
    setUploadItemArquivo(null);
    setUploadItemTitulo('');
  }
  async function salvarUploadItem() {
    if (!oport || !uploadItemId) return;
    if (!uploadItemTitulo.trim()) { message.warning('Título obrigatório'); return; }
    if (!uploadItemArquivo) { message.warning('Selecione o arquivo'); return; }
    setUploadItemSalvando(true);
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      // 1) presigned upload
      const r1 = await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'upload_url',
          filename: uploadItemArquivo.name,
          content_type: uploadItemArquivo.type || 'application/octet-stream',
          categoria_codigo: uploadItemCategoria,
        }, { headers });
      const d1 = r1.data?.body ? JSON.parse(r1.data.body) : r1.data;
      // 2) PUT direto no S3
      await axios.put(d1.upload_url, uploadItemArquivo, {
        headers: { 'Content-Type': uploadItemArquivo.type || 'application/octet-stream' },
        timeout: 180000,
      });
      // 3) cria documento com escopo da oportunidade (nao vai pro catalogo geral)
      const r2 = await axios.post(`${API_URL}/comercial/documentos`,
        { action: 'criar',
          s3_key: d1.s3_key,
          filename_original: uploadItemArquivo.name,
          titulo: uploadItemTitulo.trim(),
          categoria_codigo: uploadItemCategoria,
          eh_template: false,
          escopo_oportunidade_id: oport.id,
        }, { headers });
      const d2 = r2.data?.body ? JSON.parse(r2.data.body) : r2.data;
      // criarDocumento retorna { documento: {...}, sumarizacao_ok } — as
      // chaves id/doc.id eram de uma versão antiga do contrato (bug L7)
      const docId = d2?.documento?.id || d2?.id || d2?.doc?.id;
      if (!docId) throw new Error('Backend nao retornou id do documento criado');
      // 4) vincula ao item do checklist + marca status atende
      await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'atualizar_item_checklist',
          id: uploadItemId,
          doc_id_vinculado: docId,
          status: 'temos_atende',
        }, { headers });
      message.success('Arquivo anexado e vinculado ao item.');
      setUploadItemId(null);
      setUploadItemArquivo(null);
      setUploadItemTitulo('');
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally {
      setUploadItemSalvando(false);
    }
  }

  async function baixarPacoteHabilitacao() {
    setBaixandoZip(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'baixar_documentos_checklist', oportunidade_id: oport?.id, proposta_id: propostaId },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.download_url) {
        window.open(d.download_url, '_blank');
        const pulados = d.total_pulados || 0;
        message.success(
          pulados > 0
            ? `Pacote com ${d.total_arquivos} documento(s). ${pulados} pulados (erro de leitura).`
            : `Pacote com ${d.total_arquivos} documento(s) gerado.`
        );
      } else {
        message.error('Falha ao gerar ZIP.');
      }
    } catch (e: any) {
      const det = e.response?.data?.error || e.message;
      if (String(det).includes('Nenhum documento vinculado')) {
        message.warning('Nenhum documento vinculado ao checklist. Gere ou regenere o checklist primeiro.');
      } else {
        message.error('Erro ao gerar ZIP: ' + det);
      }
    } finally {
      setBaixandoZip(false);
    }
  }

  async function removerAnexo(id: number) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/licitacoes`,
        { action: 'deletar_anexo_edital', id },
        { headers: { Authorization: token } });
      message.success('Anexo removido.');
      await carregar();
    } catch (e: any) {
      message.error('Erro ao remover: ' + (e.response?.data?.error || e.message));
    }
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>;
  }


  // L6 (2026-06-02) — aplica filtros antes de agrupar
  const itensFiltrados = checklistItens.filter(it => {
    if (filtroStatus.length && !filtroStatus.includes(it.status)) return false;
    if (filtroCategoria.length && !filtroCategoria.includes(it.categoria || 'outros')) return false;
    if (filtroBusca.trim()) {
      const q = filtroBusca.toLowerCase();
      const alvo = `${it.descricao || ''} ${it.exigencia_edital || ''} ${it.subcategoria || ''} ${it.observacoes || ''} ${it.doc_titulo || ''}`.toLowerCase();
      if (!alvo.includes(q)) return false;
    }
    return true;
  });
  const itensPorCategoria: Record<string, ChecklistItem[]> = {};
  itensFiltrados.forEach(it => {
    const cat = it.categoria || 'outros';
    if (!itensPorCategoria[cat]) itensPorCategoria[cat] = [];
    itensPorCategoria[cat].push(it);
  });
  // Categorias presentes no checklist completo (sem filtro) — usado no Select
  const categoriasPresentes = Array.from(new Set(checklistItens.map(it => it.categoria || 'outros')));

  // ─── Tab Visão Geral ────────────────────────────────────────────────
  const tabGeral = (
    <Row gutter={16}>
      <Col span={16}>
        <Card size="small" title="Dados do edital" style={{ marginBottom: 12 }}>
          <Row gutter={[12, 8]}>
            <Col span={12}><Text type="secondary">Órgão</Text><div><Text strong>{oport?.orgao || '—'}</Text></div></Col>
            <Col span={12}><Text type="secondary">Local</Text><div><Text>{oport?.cidade || '—'}/{oport?.uf || '—'}</Text></div></Col>
            <Col span={12}><Text type="secondary">Edital</Text><div><Text>{oport?.numero_edital || '—'}</Text></div></Col>
            <Col span={12}><Text type="secondary">Modalidade</Text><div><Text>{oport?.modalidade || '—'}</Text></div></Col>
            <Col span={12}><Text type="secondary">Data abertura</Text><div><Text>{fmtDataBr(oport?.data_abertura || '') || '—'}</Text></div></Col>
            <Col span={12}><Text type="secondary">Valor estimado</Text>
              <div><Text strong style={{ color: '#0930D0' }}>
                {oport?.valor_estimado != null ? `R$ ${Number(oport.valor_estimado).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'}
              </Text></div>
            </Col>
            <Col span={24}>
              <Text type="secondary">Objeto</Text>
              <Paragraph style={{ marginTop: 4, fontSize: 12 }}>{oport?.objeto || '—'}</Paragraph>
            </Col>
          </Row>
        </Card>
        {oport?.fit_resumo && (
          <Card size="small" title={<><RobotOutlined /> Resumo da análise (Dwight)</>}>
            <Paragraph style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: 0 }}>{oport.fit_resumo}</Paragraph>
          </Card>
        )}
      </Col>
      <Col span={8}>
        <Card size="small" title="Etapa">
          <Statistic title="No pipeline de licitações" value={rotuloDe(etapaAtual) || '—'} valueStyle={{ fontSize: 14, color: etapaDe(etapaAtual)?.cor }} />
          {dias != null && <Text type="secondary" style={{ fontSize: 11 }}>{dias === 0 ? 'desde hoje' : `há ${dias} dias nesta etapa`}</Text>}
          {oport?.decisao_motivo && <div style={{ fontSize: 11, marginTop: 4 }}>Decisão: <b>{oport.decisao_motivo}</b></div>}
          {oport?.motivo_perda && <div style={{ fontSize: 11, marginTop: 4 }}>Motivo da perda: <b>{oport.motivo_perda}</b>{oport.vencedor ? <> · venceu <b>{oport.vencedor}</b></> : null}{oport.preco_vencedor != null ? <> por <b>{valorCompacto(oport.preco_vencedor)}</b></> : null}</div>}
          <Divider style={{ margin: '8px 0' }} />
          <Statistic title="Fit score" value={oport?.fit_score ?? 0} suffix="/ 100"
            valueStyle={{ color: (oport?.fit_score ?? 0) >= 70 ? '#0a7f4a' : (oport?.fit_score ?? 0) >= 40 ? '#B07500' : '#B91626' }} />
        </Card>
        <Card size="small" title="Checklist" style={{ marginTop: 12 }}>
          {ehTriagem(etapaAtual) ? (
            <Text type="secondary" style={{ fontSize: 11 }}>
              O checklist de habilitação aparece quando a licitação entra no pipeline (botão <b>Avaliar</b>).
            </Text>
          ) : (!checklistMeta || checklistMeta.status === 'pendente') && (!podeGerarChecklist || ehTerminal(etapaAtual)) ? (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {ehTerminal(etapaAtual)
                ? 'Licitação encerrada — o checklist não é mais gerado.'
                : 'Nenhum checklist gerado ainda. Quem opera o pipeline de licitações pode gerar.'}
            </Text>
          ) : !checklistMeta || checklistMeta.status === 'pendente' ? (
            <>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Nenhum checklist gerado ainda. Clique no botão pra Sonnet 4.5 cruzar o edital com o catálogo da Hydrodata.
              </Text>
              <Button type="primary" block icon={<RobotOutlined />} onClick={gerarChecklist}
                loading={gerando} style={{ marginTop: 8 }}
                disabled={!anexos.length}>
                Gerar checklist
              </Button>
              {!anexos.length && (
                <Text type="warning" style={{ fontSize: 10, display: 'block', marginTop: 4 }}>
                  ⚠ Sem PDFs do edital — rode a análise breve primeiro
                </Text>
              )}
            </>
          ) : checklistMeta.status === 'gerando' ? (
            <Alert
              type="info" showIcon icon={<Spin size="small" />}
              message="Gerando checklist..."
              description="Sonnet está analisando o edital. ~60-180s. Esta tela atualiza sozinha."
            />
          ) : checklistMeta.status === 'erro' ? (
            <>
              <Alert type="error" message="Erro ao gerar" description={checklistMeta.erro_mensagem} />
              {podeGerarChecklist && !ehTerminal(etapaAtual) && (
                <Button block icon={<ReloadOutlined />} onClick={gerarChecklist}
                  loading={gerando} style={{ marginTop: 8 }}>Tentar de novo</Button>
              )}
            </>
          ) : (
            <>
              <Statistic title="Itens completos" value={checklistMeta.itens_completos || 0}
                suffix={`/ ${checklistMeta.total_itens || 0}`} valueStyle={{ fontSize: 18 }} />
              <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 4 }}>
                Gerado {checklistMeta.gerado_em ? new Date(checklistMeta.gerado_em).toLocaleString('pt-BR') : '—'}
                {checklistMeta.custo_estimado_usd ? ` · custo ~$${checklistMeta.custo_estimado_usd}` : ''}
              </Text>
              <Button block type="primary" icon={<DownloadOutlined />}
                onClick={baixarPacoteHabilitacao} loading={baixandoZip}
                style={{ marginTop: 8 }} size="small">
                Baixar pacote habilitação (.zip)
              </Button>
              {podeGerarChecklist && !ehTerminal(etapaAtual) && (
                <Button block icon={<ReloadOutlined />} onClick={gerarChecklist}
                  loading={gerando} style={{ marginTop: 6 }} size="small">Regenerar</Button>
              )}
            </>
          )}
        </Card>
      </Col>
    </Row>
  );

  // ─── Tab Documentos do Edital ──────────────────────────────────────
  const tabDocs = (
    <Card size="small"
      extra={
        <Space size={6}>
          <Button size="small" icon={<RobotOutlined />} onClick={abrirAddTemplateLic}
            disabled={!oport}>
            + Template específico
          </Button>
          <Button size="small" type="primary" icon={<PlusOutlined />}
            onClick={() => setUploadModal(true)} disabled={!oport}>
            Anexar PDF
          </Button>
        </Space>
      }>
      {anexos.length === 0 ? (
        <Empty description={
          <>
            <div>Nenhum documento anexado.</div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Você pode rodar "Análise breve" no pipeline pra baixar do PNCP, ou clicar em "Anexar PDF" pra subir manualmente.
            </Text>
          </>
        } />
      ) : (
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>{anexos.length} documento(s):</Text>
          <div style={{ marginTop: 8 }}>
            {anexos.map(a => {
              const isManual = a.metadados?.upload_manual === true;
              return (
                <div key={a.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: 10,
                  border: '1px solid #e8e8e8', borderRadius: 4, marginBottom: 6,
                  background: isManual ? '#FFF7E6' : undefined,
                }}>
                  <FileTextOutlined style={{ fontSize: 20, color: '#0930D0' }} />
                  <div style={{ flex: 1 }}>
                    <Text strong>{a.nome || '(sem nome)'}</Text>
                    <div>
                      <Tag color={isManual ? 'orange' : 'blue'} style={{ fontSize: 9 }}>
                        {a.tipo || 'sem tipo'}{isManual ? ' · manual' : ' · PNCP'}
                      </Tag>
                      {a.exigencias_status === 'pronto' && (
                        <Tag color="green" style={{ fontSize: 9 }}>
                          {a.exigencias_total ?? 0} exigência(s) extraída(s)
                        </Tag>
                      )}
                      {a.exigencias_status === 'extraindo' && (
                        <Tag color="blue" style={{ fontSize: 9 }}>
                          <Spin size="small" /> extraindo...
                        </Tag>
                      )}
                      {a.exigencias_status === 'erro' && (
                        <Tooltip title={a.exigencias_erro || 'Erro na extração'}>
                          <Tag color="red" style={{ fontSize: 9 }}>erro na extração</Tag>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <Button size="small" icon={<DownloadOutlined />} onClick={() => baixarAnexo(a)}
                    disabled={!a.download_url}>Abrir</Button>
                  <Popconfirm title="Remover este anexo?" onConfirm={() => removerAnexo(a.id)}
                    okText="Remover" cancelText="Cancelar">
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );

  // ─── Tab Checklist ──────────────────────────────────────────────────
  const tabChecklist = (
    <>
      {!checklistMeta || checklistMeta.status !== 'pronto' ? (
        <Empty description={
          checklistMeta?.status === 'gerando'
            ? <span><Spin size="small" /> Gerando checklist... atualiza sozinho</span>
            : 'Nenhum checklist disponível. Volte na aba Visão Geral e clique em "Gerar checklist".'
        } />
      ) : (
        <>
          {/* L6 (2026-06-02) — barra de filtros */}
          <Card size="small" style={{ marginBottom: 12, background: '#FAFBFF' }}
            bodyStyle={{ padding: '10px 12px' }}>
            <Space size={8} wrap style={{ width: '100%' }}>
              <Input.Search
                placeholder="Buscar item, exigência, observação, doc…"
                value={filtroBusca} onChange={e => setFiltroBusca(e.target.value)}
                allowClear style={{ width: 260 }} size="small"
              />
              <Select mode="multiple" placeholder="Status"
                value={filtroStatus} onChange={setFiltroStatus}
                style={{ minWidth: 180 }} size="small" allowClear
                options={Object.entries(STATUS_CFG).map(([k, v]) => ({
                  label: v.label, value: k,
                }))}
              />
              <Select mode="multiple" placeholder="Categoria"
                value={filtroCategoria} onChange={setFiltroCategoria}
                style={{ minWidth: 200 }} size="small" allowClear
                options={categoriasPresentes.map(c => ({
                  label: CATEGORIA_LABEL[c] || c, value: c,
                }))}
              />
              {(filtroStatus.length > 0 || filtroCategoria.length > 0 || filtroBusca) && (
                <Button size="small" type="link"
                  onClick={() => { setFiltroStatus([]); setFiltroCategoria([]); setFiltroBusca(''); }}>
                  Limpar filtros
                </Button>
              )}
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>
                Mostrando {itensFiltrados.length} de {checklistItens.length} item(s)
              </Text>
            </Space>
          </Card>
          {itensFiltrados.length === 0 ? (
            <Empty description="Nenhum item com esses filtros" />
          ) : (
            Object.entries(itensPorCategoria).map(([cat, itens]) => (
          <Card key={cat} size="small" style={{ marginBottom: 12 }}
            title={<Space><Text strong>{CATEGORIA_LABEL[cat] || cat}</Text>
              <Tag color="default">{itens.length} item(ns)</Tag></Space>}>
            {itens.map(item => {
              const cfg = STATUS_CFG[item.status] || STATUS_CFG.pendente;
              return (
                <div key={item.id} style={{
                  padding: 10, borderLeft: `4px solid ${cfg.color === 'default' ? '#888' :
                    cfg.color === 'green' ? '#0a7f4a' : cfg.color === 'orange' ? '#B07500' :
                    cfg.color === 'red' ? '#B91626' : '#0930D0'}`,
                  background: '#fafafa', marginBottom: 8, borderRadius: 3,
                }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <Tag color={cfg.color} icon={cfg.icon} style={{ minWidth: 90, textAlign: 'center' }}>
                      {cfg.label}
                    </Tag>
                    <div style={{ flex: 1 }}>
                      <Text strong>{item.descricao}</Text>
                      {item.subcategoria && (
                        <Tag style={{ fontSize: 9, marginLeft: 6 }}>{item.subcategoria}</Tag>
                      )}
                      {item.editado_manualmente && (
                        <Tooltip title="Editado manualmente">
                          <EditOutlined style={{ marginLeft: 6, color: '#aaa', fontSize: 10 }} />
                        </Tooltip>
                      )}
                      {item.exigencia_edital && (
                        <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                          <Text type="secondary">Edital pede: </Text>{item.exigencia_edital}
                        </div>
                      )}
                      {/* L8 (2026-06-02) — rastreabilidade: qual anexo originou */}
                      {item.anexo_origem_nome && (
                        <div style={{ marginTop: 4 }}>
                          <Tooltip title={`Item extraído do anexo: ${item.anexo_origem_nome}${item.anexo_origem_tipo ? ' — ' + item.anexo_origem_tipo : ''}. Clique pra abrir o PDF.`}>
                            <Tag color="blue" bordered={false}
                              style={{ fontSize: 9, cursor: item.anexo_origem_download_url ? 'pointer' : 'default' }}
                              onClick={item.anexo_origem_download_url
                                ? (e) => { e.stopPropagation(); window.open(item.anexo_origem_download_url!, '_blank'); }
                                : undefined}>
                              📄 {item.anexo_origem_nome}
                            </Tag>
                          </Tooltip>
                        </div>
                      )}
                      {item.doc_codigo && (
                        <div style={{ fontSize: 11, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Tag color="purple" style={{ fontSize: 9, margin: 0 }}>{item.doc_codigo}</Tag>
                          <Text style={{ fontSize: 11 }}>{item.doc_titulo}</Text>
                          {item.doc_eh_template && (
                            <Tag color="gold" style={{ fontSize: 9, margin: 0 }}>template</Tag>
                          )}
                          {item.doc_download_url && (
                            <Tooltip title={`Baixar ${item.doc_arquivo_nome || 'documento'}`}>
                              <Button size="small" type="link" icon={<DownloadOutlined />}
                                onClick={(e) => { e.stopPropagation(); window.open(item.doc_download_url!, '_blank'); }}
                                style={{ padding: '0 4px', height: 'auto', fontSize: 11 }}>
                                Baixar
                              </Button>
                            </Tooltip>
                          )}
                          {item.doc_eh_template && (
                            <Button size="small" type="link" icon={<RobotOutlined />}
                              onClick={(e) => { e.stopPropagation(); gerarDocxTemplate(item); }}
                              loading={gerandoDocxItemId === item.id}
                              style={{ padding: '0 4px', height: 'auto', fontSize: 11, color: '#B07500' }}>
                              Gerar com IA
                            </Button>
                          )}
                        </div>
                      )}
                      {item.observacoes && (
                        <Paragraph style={{ fontSize: 11, marginTop: 4, marginBottom: 0, color: '#666' }}>
                          {item.observacoes}
                        </Paragraph>
                      )}
                      <Space size={4} style={{ marginTop: 6 }} wrap>
                        <Select value={item.status} size="small" style={{ width: 140 }}
                          onChange={(v) => atualizarItem(item.id, 'status', v)}
                          options={Object.entries(STATUS_CFG).map(([k, v]) => ({ label: v.label, value: k }))} />
                        <Input.TextArea size="small" placeholder="Observações..."
                          value={item.observacoes || ''}
                          onBlur={(e) => {
                            if (e.target.value !== (item.observacoes || '')) {
                              atualizarItem(item.id, 'observacoes', e.target.value);
                            }
                          }}
                          autoSize={{ minRows: 1, maxRows: 2 }}
                          style={{ width: 360, fontSize: 11 }} />
                        {/* L7 (2026-06-02) — anexar arquivo especifico do edital pra este item */}
                        <Button size="small" icon={<UploadOutlined />}
                          onClick={(e) => { e.stopPropagation(); abrirUploadItem(item); }}
                          title="Anexar arquivo específico desta licitação (não vai pro catálogo geral)">
                          {item.doc_id_vinculado ? 'Trocar arquivo' : 'Anexar arquivo'}
                        </Button>
                      </Space>
                    </div>
                  </div>
                </div>
              );
            })}
          </Card>
        ))
          )}
        </>
      )}
    </>
  );

  if (!loading && !oport) {
    return (
      <div style={{ width: '100%', padding: '16px 24px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onVoltar}>Voltar ao pipeline</Button>
        <Empty style={{ marginTop: 24 }} description="Licitação não encontrada (ou sem permissão para ver)." />
      </div>
    );
  }

  const terminal = ehTerminal(etapaAtual);
  const emTriagem = ehTriagem(etapaAtual);
  return (
    <div style={{ width: '100%', padding: '16px 24px' }}>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={onVoltar}>Voltar ao pipeline</Button>
        {oport?.conlic_url && <Button size="small" href={oport.conlic_url} target="_blank" rel="noreferrer">Ver no portal</Button>}
      </Space>
      <Title level={4} style={{ margin: 0, color: '#020D7F' }}>
        {oport?.numero_edital || 'Edital sem número'} — {oport?.orgao || '—'}
        <Tag color="purple" style={{ marginLeft: 8, fontSize: 10 }}>Licitação</Tag>
        {oport?.modalidade && <Tag style={{ fontSize: 10 }}>{oport.modalidade}</Tag>}
      </Title>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {[oport?.cidade, oport?.uf].filter(Boolean).join('/') || 'local não informado'}
        {oport?.valor_estimado != null && <> · estimado <b>{valorCompacto(oport.valor_estimado)}</b></>}
        {propostaId && <> · negócio gêmeo #{propostaId}</>}
      </Text>

      {/* ─── O processo: trilha, prazo, responsável e decisões (02/10/2026) ─── */}
      <Card size="small" style={{ marginTop: 12 }} styles={{ body: { padding: 12 } }}>
        {emTriagem ? (
          <Alert type="info" showIcon message="Ainda na triagem"
            description="Esta licitação não entrou no pipeline. Avaliar põe em 'Avaliando', com responsável, e liga os avisos de prazo."
            action={podeOperar ? <Button type="primary" size="small" loading={movendo} onClick={avaliar}>Avaliar</Button> : undefined} />
        ) : (
          <TrilhaLicitacao status={etapaAtual} diasNaEtapa={dias} onMover={pedirMovimento} podeOperar={podeOperar && !movendo} />
        )}
        {terminal && (
          <Text style={{ display: 'block', marginTop: 8, fontSize: 12.5 }}>
            <Tag color={etapaAtual === 'ganho' ? 'success' : 'error'} style={{ marginRight: 8 }}>{rotuloDe(etapaAtual)}</Tag>
            {etapaAtual === 'ganho' && oport?.valor_contratado != null && <>Contratado por <b>{valorCompacto(oport.valor_contratado)}</b></>}
            {oport?.motivo_perda && <>Motivo: <b>{oport.motivo_perda}</b></>}
            {oport?.decisao_motivo && etapaAtual === 'nao_participamos' && <>Motivo: <b>{oport.decisao_motivo}</b></>}
          </Text>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px', marginTop: 12, alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#888' }}>Próximo prazo</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: prazo ? prazo.classe.cor : '#9AA0B4' }}>
              {prazo ? `${prazo.classe.icone}${prazo.rotulo} ${prazo.classe.rotulo}` : 'sem prazo'}
            </div>
            {prazo && <Text type="secondary" style={{ fontSize: 11 }}>{prazo.classe.data.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Text>}
          </div>
          <div style={{ minWidth: 240 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: '#888' }}>Responsável</div>
            <Select size="small" style={{ width: 260 }} placeholder="Sem responsável" allowClear showSearch optionFilterProp="label"
              disabled={!podeOperar || movendo} value={oport?.responsavel_email || undefined}
              onChange={(v) => trocarResponsavel(v)}
              options={Array.from(new Set([...(vendedores.map(v => v.email)), oport?.responsavel_email].filter(Boolean))).map(e => {
                const v = vendedores.find(x => x.email === e);
                return { value: e as string, label: v ? `${v.nome} (${v.email})` : (e as string) };
              })} />
          </div>
          {podeOperar && !emTriagem && (
            <Space wrap style={{ marginLeft: 'auto' }}>
              {etapaAtual === 'avaliando' && (
                <Button type="primary" loading={movendo} onClick={participar} style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>Vamos participar</Button>
              )}
              {(etapaAtual === 'avaliando' || etapaAtual === 'preparacao') && (
                <Button loading={movendo} onClick={() => setFechando('nao_participamos')}>Não participamos</Button>
              )}
              {ehAberta(etapaAtual) && !['avaliando', 'preparacao'].includes(etapaAtual) && (
                <Button danger loading={movendo} onClick={() => setFechando('perdido')}>Perdido</Button>
              )}
              {['sessao', 'habilitacao_recursos', 'homologacao'].includes(etapaAtual) && (
                <Button type="primary" loading={movendo} onClick={() => setFechando('ganho')}>Contrato assinado</Button>
              )}
              {terminal && (
                <Button loading={movendo} onClick={() => pedirMovimento(etapaAtual === 'ganho' ? 'homologacao' : 'preparacao')}>
                  {etapaAtual === 'ganho' ? 'Desfazer assinatura' : 'Reabrir'}
                </Button>
              )}
            </Space>
          )}
        </div>
      </Card>

      <Tabs defaultActiveKey="geral" style={{ marginTop: 12 }} items={[
        { key: 'geral', label: 'Visão Geral', children: tabGeral },
        { key: 'docs',  label: `Documentos do Edital (${anexos.length})`, children: tabDocs },
        { key: 'check', label: <span>Checklist de Habilitação{checklistMeta?.status === 'pronto' && (
          <Tag color="green" style={{ marginLeft: 6, fontSize: 9 }}>
            {checklistMeta.itens_completos}/{checklistMeta.total_itens}
          </Tag>
        )}</span>, children: tabChecklist },
        // L2 (2026-06-02): aba composicoes — reusa AbaComposicoes do privado.
        // Negocio precisa de propostaId; reusa o ID que ja temos no escopo.
        { key: 'comp', label: 'Composições', children: !propostaId ? (
          <Empty description="A composição de preço aparece a partir da etapa Proposta (quando o negócio gêmeo nasce)." />
        ) : (
          <AbaComposicoes
            negocioId={propostaId}
            onNova={() => setModalNovaComposicao(true)}
            onAbrir={(id) => setComposicaoAbertaId(id)}
            reloadFlag={reloadComposicoes} />
        ) },
      ]} />

      {fechando && oport && (
        <ModalFecharLicitacao tipo={fechando} titulo={`${oport.numero_edital || 'Edital'} — ${oport.orgao || ''}`}
          valorEstimado={oport.valor_estimado != null ? Number(oport.valor_estimado) : null}
          onCancelar={() => setFechando(null)} onConfirmar={confirmarFechamento} />
      )}

      <Modal open={uploadModal} onCancel={() => !uploadando && setUploadModal(false)}
        onOk={uploadAnexoManual} confirmLoading={uploadando}
        title="Anexar documentos do edital"
        okText={uploadFiles.length > 1 ? `Enviar ${uploadFiles.length} arquivos` : 'Enviar'}
        cancelText="Cancelar"
        okButtonProps={{ disabled: !uploadFiles.length }}>
        <Form layout="vertical" size="small">
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="Upload em lote"
            description="Selecione vários PDFs de uma vez. O tipo (Edital, TR, Projeto Básico, etc.) é inferido pelo nome do arquivo." />
          <Form.Item label="Arquivos (PDF)" required>
            <Upload.Dragger multiple beforeUpload={(file, fileList) => {
              setUploadFiles(prev => {
                const ja = new Set(prev.map(f => f.name + ':' + f.size));
                const novos = fileList.filter(f => !ja.has(f.name + ':' + f.size));
                return [...prev, ...novos];
              });
              return false;
            }}
              onRemove={(file) => {
                setUploadFiles(prev => prev.filter(f => !(f.name === file.name && f.size === file.size)));
              }}
              fileList={uploadFiles.map((f, i) => ({
                uid: String(i), name: f.name, size: f.size, status: 'done',
              } as any))}
              accept=".pdf,application/pdf"
              disabled={uploadando}>
              <p><UploadOutlined style={{ fontSize: 28, color: '#0930D0' }} /></p>
              <p style={{ fontSize: 13 }}>Arraste PDFs aqui ou clique para selecionar</p>
              <p style={{ fontSize: 11, color: '#888' }}>O tipo será inferido do nome do arquivo</p>
            </Upload.Dragger>
          </Form.Item>
          {uploadProgress && (
            <Alert type="info" message={<Space><Spin size="small" /><Text style={{ fontSize: 12 }}>{uploadProgress}</Text></Space>} />
          )}
        </Form>
      </Modal>

      {/* L7 (2026-06-02) — Modal: anexar arquivo especifico ao item do checklist */}
      <Modal open={uploadItemId != null} onCancel={fecharUploadItem}
        onOk={salvarUploadItem} confirmLoading={uploadItemSalvando}
        title="Anexar arquivo específico ao item do checklist"
        okText="Enviar e vincular" cancelText="Cancelar"
        okButtonProps={{ disabled: !uploadItemArquivo || !uploadItemTitulo.trim() }}
        width={520} destroyOnClose>
        <Alert type="info" showIcon style={{ marginBottom: 12, fontSize: 12 }}
          message="Arquivo específico desta licitação"
          description="O documento fica vinculado SÓ a este edital (não vai pro catálogo geral da empresa). Útil pra declarações pedidas especificamente neste edital." />
        <Form layout="vertical" size="small">
          <Form.Item label="Item do checklist">
            <Text type="secondary" style={{ fontSize: 12 }}>{uploadItemDescricao}</Text>
          </Form.Item>
          <Form.Item label="Título do documento" required>
            <Input value={uploadItemTitulo}
              onChange={e => setUploadItemTitulo(e.target.value)}
              placeholder="Ex.: Declaração específica do edital PE 002/2026" />
          </Form.Item>
          <Form.Item label="Arquivo" required>
            <Upload beforeUpload={(file) => { setUploadItemArquivo(file as File); return false; }}
              maxCount={1} onRemove={() => setUploadItemArquivo(null)}
              fileList={uploadItemArquivo
                ? [{ uid: '1', name: uploadItemArquivo.name, status: 'done' } as any]
                : []}>
              <Button icon={<UploadOutlined />}>Selecionar arquivo</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* L2 (2026-06-02) — modais de composicao inline (mesmo padrao do privado) */}
      {modalNovaComposicao && propostaId != null && (
        <ModalNovaComposicao
          propostaId={propostaId}
          tituloNegocio={`${oport?.numero_edital || 'Edital'} — ${(oport?.objeto || '').slice(0, 80)}`}
          onClose={() => setModalNovaComposicao(false)}
          onCriada={(composicaoId) => {
            setModalNovaComposicao(false);
            setReloadComposicoes(f => f + 1);
            setComposicaoAbertaId(composicaoId);
          }}
        />
      )}
      {composicaoAbertaId && (
        <EditorComposicaoInline
          composicaoId={composicaoAbertaId}
          onClose={() => setComposicaoAbertaId(null)}
          onSaved={() => {
            setComposicaoAbertaId(null);
            setReloadComposicoes(f => f + 1);
          }}
        />
      )}

      {/* F10 — Modal "Adicionar template específico" */}
      <Modal open={templateLicModal} onCancel={() => !tplSalvando && setTemplateLicModal(false)}
        onOk={salvarTemplateLic} confirmLoading={tplSalvando}
        title="Adicionar modelo editável pela IA (específico desta licitação)"
        okText="Salvar" cancelText="Cancelar" width={560} destroyOnClose>
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Quando regenerar o checklist, o Sonnet pode vincular este modelo a um item — aí aparece o botão 'Gerar com IA' que preenche o .docx com dados da empresa + licitação."
          description="Use pra declarações específicas cobradas por este edital (não vai pro catálogo da empresa)." />
        <Form layout="vertical" size="small">
          <Form.Item label="Título do modelo" required>
            <Input value={tplTitulo} onChange={e => setTplTitulo(e.target.value)}
              placeholder='Ex.: "Declaração de não impedimento ME/EPP"' />
          </Form.Item>
          <Form.Item label="Categoria" required>
            <Select value={tplCategoria} onChange={setTplCategoria}
              options={tplCategorias.length
                ? tplCategorias.map(c => ({ label: c.nome, value: c.codigo }))
                : [{ label: 'Outros', value: 'outros' }]} />
          </Form.Item>
          <Form.Item label="Arquivo (.docx)" required>
            <Upload beforeUpload={(file) => { setTplArquivo(file); return false; }}
              maxCount={1} onRemove={() => setTplArquivo(null)}
              accept=".docx"
              fileList={tplArquivo ? [{ uid: '1', name: tplArquivo.name, status: 'done' } as any] : []}>
              <Button icon={<UploadOutlined />}>Selecionar .docx</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
